import { makeAutoObservable } from 'mobx'
import type {
  AnalyticsChartPeriod,
  IConversionDetailAnalyticsResponse,
  IConversionDetailsStatisticResponse,
  IConversionDetailsChartResponse,
  ProcessedStatistic,
} from 'entities/Analytics/api/types'
import { formatDateForDisplay, calculateTrend, computeHasStatistic } from 'pages/analytics/lib'

interface ConversionDetailsRechartsDataPoint {
  date: string
  name: string
  total: number
}

export class ConversionDetailsChartDataStore {
  private _rawStatistic: IConversionDetailsStatisticResponse[] = []
  private _rawChartData: IConversionDetailsChartResponse[] = []
  private _period: AnalyticsChartPeriod | null = null

  constructor() {
    makeAutoObservable(this)
  }

  setRawData = (response: IConversionDetailAnalyticsResponse, period?: AnalyticsChartPeriod) => {
    this._rawStatistic = response.statistic
    this._rawChartData = response.charts
    if (period) {
      this._period = period
    }
  }

  get processedStatistic(): ProcessedStatistic[] {
    return this._rawStatistic.map((item) => {
      const { key, value, prevValue, label } = item
      const trend = calculateTrend(value, prevValue)

      return {
        key: key,
        label: label || key,
        value: value,
        prevValue: prevValue,
        trend,
      }
    })
  }

  get rechartsData(): ConversionDetailsRechartsDataPoint[] {
    if (!this._rawChartData) {
      return []
    }
    return this._rawChartData
      .map((item) => ({
        date: item.date,
        name: formatDateForDisplay(item.date, this._period ?? 'week'),
        total: item.total ?? 0,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  getStatisticByKey = (key: string): ProcessedStatistic | undefined => {
    return this.processedStatistic.find((item) => item.key === key)
  }

  get hasStatistic() {
    return computeHasStatistic(this._rawStatistic)
  }

  get hasChartData() {
    return this._rawChartData && this._rawChartData.length > 0
  }

  dispose = () => {
    this._rawStatistic = []
    this._rawChartData = []
    this._period = null
  }
}
